/**
 * Top-Level Application Mode & Defter Workspace Controller (§Top-level modes, §Defter workspace, Gate NT-2).
 *
 * Implements:
 * - Two distinct top-level modes: 'Dönüştür' (convert) and 'Defter' (notebook).
 * - W3C ARIA TabList keyboard accessibility (ArrowLeft, ArrowRight, Home, End).
 * - Automatic visibility toggling: conversion controls and source comparison
 *   are hidden in Defter mode while the notebook vector canvas expands to fill the viewport.
 * - Synchronization of compact tablet tools (Pen, Eraser, Select, Lasso) with editor state.
 * - Document title, branch identity, and sync state badge updates.
 */

export type AppMode = 'convert' | 'notebook';
export type ToolName = 'select' | 'lasso' | 'draw' | 'erase';

export interface NotebookDOMRefs {
  tabConvert: HTMLElement;
  tabNotebook: HTMLElement;
  notebookToolbar?: HTMLElement | null;
  bodyElement?: HTMLElement | null;
  notebookDocTitle?: HTMLElement | null;
  notebookSyncBadge?: HTMLElement | null;
  notebookBranchBadge?: HTMLElement | null;
  notebookSelectionInfo?: HTMLElement | null;
  toolSelect?: HTMLSelectElement | null;
  toolButtons?: {
    select?: HTMLElement | null;
    lasso?: HTMLElement | null;
    pen?: HTMLElement | null;
    eraser?: HTMLElement | null;
  };
}

export interface NotebookModeCallbacks {
  onModeChange?: (mode: AppMode) => void;
  onToolChange?: (tool: ToolName) => void;
  onOpenLibrary?: () => void;
  onSaveCloud?: () => void;
  onExportSvg?: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
}

export class NotebookModeController {
  private mode: AppMode = 'convert';
  private refs: NotebookDOMRefs;
  private callbacks: NotebookModeCallbacks;

  constructor(refs: NotebookDOMRefs, callbacks: NotebookModeCallbacks = {}) {
    this.refs = refs;
    this.callbacks = callbacks;
    this.setupEventListeners();
    this.applyMode(this.mode);
  }

  public getMode(): AppMode {
    return this.mode;
  }

  public setMode(newMode: AppMode): void {
    if (this.mode === newMode) return;
    this.mode = newMode;
    this.applyMode(newMode);
    this.callbacks.onModeChange?.(newMode);
  }

  public updateDocumentHeader(info: {
    title?: string;
    branchName?: string;
    syncState?: 'clean' | 'dirty' | 'saving' | 'conflict' | 'offline' | 'error';
    selectedCount?: number;
  }): void {
    if (this.refs.notebookDocTitle && info.title !== undefined) {
      this.refs.notebookDocTitle.textContent = `Belge: ${info.title || 'Başlıksız'}`;
    }

    if (this.refs.notebookBranchBadge && info.branchName !== undefined) {
      this.refs.notebookBranchBadge.textContent = `Branch: ${info.branchName}`;
    }

    if (this.refs.notebookSyncBadge && info.syncState !== undefined) {
      switch (info.syncState) {
        case 'clean':
          this.refs.notebookSyncBadge.textContent = 'Kaydedildi';
          this.refs.notebookSyncBadge.style.background = '#285c4d';
          break;
        case 'dirty':
          this.refs.notebookSyncBadge.textContent = 'Taslak';
          this.refs.notebookSyncBadge.style.background = '#64748b';
          break;
        case 'saving':
          this.refs.notebookSyncBadge.textContent = 'Kaydediliyor...';
          this.refs.notebookSyncBadge.style.background = '#0284c7';
          break;
        case 'conflict':
          this.refs.notebookSyncBadge.textContent = 'Çatışma (409)';
          this.refs.notebookSyncBadge.style.background = '#dc2626';
          break;
        case 'offline':
          this.refs.notebookSyncBadge.textContent = 'Çevrimdışı';
          this.refs.notebookSyncBadge.style.background = '#d97706';
          break;
        default:
          this.refs.notebookSyncBadge.textContent = 'Hata';
          this.refs.notebookSyncBadge.style.background = '#dc2626';
      }
    }

    if (this.refs.notebookSelectionInfo && info.selectedCount !== undefined) {
      this.refs.notebookSelectionInfo.textContent =
        info.selectedCount > 0 ? `Seçim: ${info.selectedCount} nesne` : 'Seçim: Yok';
    }
  }

  public setActiveTool(tool: ToolName): void {
    const btns = this.refs.toolButtons;
    if (btns) {
      if (btns.select) {
        btns.select.classList.toggle('active', tool === 'select');
        btns.select.setAttribute('aria-pressed', String(tool === 'select'));
      }
      if (btns.lasso) {
        btns.lasso.classList.toggle('active', tool === 'lasso');
        btns.lasso.setAttribute('aria-pressed', String(tool === 'lasso'));
      }
      if (btns.pen) {
        btns.pen.classList.toggle('active', tool === 'draw');
        btns.pen.setAttribute('aria-pressed', String(tool === 'draw'));
      }
      if (btns.eraser) {
        btns.eraser.classList.toggle('active', tool === 'erase');
        btns.eraser.setAttribute('aria-pressed', String(tool === 'erase'));
      }
    }

    if (this.refs.toolSelect && this.refs.toolSelect.value !== tool) {
      this.refs.toolSelect.value = tool;
    }

    this.callbacks.onToolChange?.(tool);
  }

  private applyMode(mode: AppMode): void {
    const isNotebook = mode === 'notebook';

    // Update Tab states
    this.refs.tabConvert.setAttribute('aria-selected', String(!isNotebook));
    this.refs.tabConvert.classList.toggle('active', !isNotebook);

    this.refs.tabNotebook.setAttribute('aria-selected', String(isNotebook));
    this.refs.tabNotebook.classList.toggle('active', isNotebook);

    // Update body classes for layout expansion
    const body = this.refs.bodyElement || (typeof document !== 'undefined' ? document.body : null);
    if (body) {
      if (isNotebook) {
        body.classList.remove('mode-convert');
        body.classList.add('mode-defter');
      } else {
        body.classList.remove('mode-defter');
        body.classList.add('mode-convert');
      }
    }

    // Toggle notebook toolbar visibility directly as fallback
    if (this.refs.notebookToolbar) {
      this.refs.notebookToolbar.style.display = isNotebook ? 'flex' : 'none';
    }
  }

  private setupEventListeners(): void {
    // Click listeners on tabs
    this.refs.tabConvert.addEventListener('click', () => this.setMode('convert'));
    this.refs.tabNotebook.addEventListener('click', () => this.setMode('notebook'));

    // Keyboard accessibility on tabs
    const handleKeyNav = (ev: KeyboardEvent) => {
      if (ev.key === 'ArrowRight' || ev.key === 'ArrowDown') {
        ev.preventDefault();
        this.setMode('notebook');
        this.refs.tabNotebook.focus?.();
      } else if (ev.key === 'ArrowLeft' || ev.key === 'ArrowUp') {
        ev.preventDefault();
        this.setMode('convert');
        this.refs.tabConvert.focus?.();
      } else if (ev.key === 'Home') {
        ev.preventDefault();
        this.setMode('convert');
        this.refs.tabConvert.focus?.();
      } else if (ev.key === 'End') {
        ev.preventDefault();
        this.setMode('notebook');
        this.refs.tabNotebook.focus?.();
      }
    };

    this.refs.tabConvert.addEventListener('keydown', handleKeyNav as any);
    this.refs.tabNotebook.addEventListener('keydown', handleKeyNav as any);

    // Tool button click listeners
    const btns = this.refs.toolButtons;
    if (btns) {
      btns.select?.addEventListener('click', () => this.setActiveTool('select'));
      btns.lasso?.addEventListener('click', () => this.setActiveTool('lasso'));
      btns.pen?.addEventListener('click', () => this.setActiveTool('draw'));
      btns.eraser?.addEventListener('click', () => this.setActiveTool('erase'));
    }
  }
}
